import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tipsRequest } from "../helpers/tipsWorkflow";
import { prepareRecentGameDetail } from "../helpers/recentTeamGames";
import {
  buildRecentPerformance,
  selectRecentGames,
} from "../helpers/recentTeamGames";

// Read-only context shared by the league table and AI detail. No setting writes.
export default function TeamContext({
  team,
  seasonId,
  expectedLedger = [],
  shouldLedger = [],
  initialLimit = 8,
}) {
  const { t: tr, i18n } = useTranslation();
  const t = (k, args) => tr(`help.teamContext.${k}`, args);
  const [scope, setScope] = useState("overall"),
    [limit, setLimit] = useState(initialLimit),
    [games, setGames] = useState(null),
    [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setGames(null);
    setError(false);
    setScope("overall");
    if (Array.isArray(team?.recent_games)) {
      setGames(team.recent_games);
      return;
    }
    if (!seasonId || !team?.team_id) return;
    tipsRequest("get-standings", { season_id: seasonId, team_id: team.team_id })
      .then((rows) => {
        if (active)
          setGames(
            (Array.isArray(rows) ? rows : []).map((row) =>
              prepareRecentGameDetail(row, team.team_id)
            )
          );
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [seasonId, team?.team_id, team?.recent_games]);
  const actual = team?.references?.actual;
  const values = scope === "overall" ? actual : actual?.[scope];
  const fmt = (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? new Intl.NumberFormat(i18n.language, {
          maximumFractionDigits: 2,
        }).format(v)
      : "—";
  const recent = selectRecentGames(
    team?.recent_games
      ? games
      : buildRecentPerformance(games, expectedLedger, shouldLedger),
    limit
  );
  return (
    <section className="team-context">
      <div className="tc-actions" aria-label={t("scope")}>
        {["overall", "home", "away"].map((k) => (
          <button
            key={k}
            aria-pressed={scope === k}
            onClick={() => setScope(k)}
          >
            {t(k)}
          </button>
        ))}
      </div>
      <p>
        {t("position")}: {fmt(actual?.position)} · {t("played")}:{" "}
        {fmt(values?.played)} · {t("wdl")}: {fmt(values?.won)} /{" "}
        {fmt(values?.draw)} / {fmt(values?.lost)} · {t("goals")}:{" "}
        {fmt(values?.goalsScored)} : {fmt(values?.goalsAgainst)}
      </p>
      <h4>{t("recent")}</h4>
      <label>
        {t("count")}{" "}
        <select
          aria-label={t("count")}
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          {[4, 5, 6, 7, 8].map((n) => (
            <option key={n}>{n}</option>
          ))}
        </select>
      </label>
      <span> {t("available", { count: recent.length })}</span>
      {error ? (
        <p role="status">{t("unavailable")}</p>
      ) : games === null ? (
        <p role="status">{t("loading")}</p>
      ) : (
        <div className="le-scroll">
          <table>
            <thead>
              <tr>
                {["date", "opponent", "result", "form", "performance"].map(
                  (k) => (
                    <th key={k}>{t(k)}</th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {recent.map((g) => (
                <tr key={g.fixtureId}>
                  <td>
                    {g.startingAt
                      ? new Date(g.startingAt).toLocaleDateString(i18n.language)
                      : "—"}
                  </td>
                  <td>
                    {g.opponent} ·{" "}
                    {t(g.homeId === team.team_id ? "home" : "away")}
                  </td>
                  <td>{g.score}</td>
                  <td>{g.form || "—"}</td>
                  <td>{fmt(g.performanceIndex)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!recent.length && <p>{t("empty")}</p>}
        </div>
      )}
      <small>{t("performanceHint")}</small>
    </section>
  );
}
